// AddBuyerLeadModal — captures a buyer who has expressed interest on a
// property case. Form-only for now; a follow-up will wire createThread.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AIHint,
  Btn,
  Chip,
  Field,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalShell,
  TextArea,
  TextInput,
} from "./ModalShell";
import type { AddBuyerLeadCtx } from "./FlowsProvider";
import { createThread, PIPELINE_STAGES, type PipelineStage } from "@/lib/proagentApi";
import { invalidateCases } from "@/lib/agentCases";

const SOURCES = ["Direct", "PropertyGuru", "99.co", "Referral", "Walk-in", "Repeat"];

type Form = {
  name?: string;
  phone?: string;
  source?: string;
  stage?: PipelineStage;
  notes?: string;
};

export function AddBuyerLeadModal({
  open,
  ctx,
  onClose,
}: {
  open: boolean;
  ctx: AddBuyerLeadCtx | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<Form>({ stage: "new_enquiry" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({ stage: "new_enquiry" });
      setError(null);
    }
  }, [open]);

  // Threads are explicitly tied to a property listing; for orphan
  // adds we still create the thread but with linked_listing_id null.
  const linkedListingId =
    ctx?.caseObj?.type === "property" ? ctx.caseObj.listingId : undefined;

  return (
    <ModalShell open={open} onClose={onClose} width={620} label="Add buyer lead">
      <ModalHeader
        eyebrow={t("agent.flows.addLead.eyebrow", { defaultValue: "Add buyer lead" })}
        title={t("agent.flows.addLead.title", { defaultValue: "New buyer in the pipeline" })}
        hint={
          ctx?.caseObj?.shortName ? (
            <>
              {t("agent.flows.for", { defaultValue: "For" })}{" "}
              <span style={{ color: "var(--ag-accent)", fontWeight: 600 }}>
                {ctx.caseObj.shortName}
              </span>
            </>
          ) : (
            t("agent.flows.addLead.hint", {
              defaultValue: "Add a buyer who has expressed interest.",
            })
          )
        }
        onClose={onClose}
      />

      <ModalBody>
        <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 12 }}>
          <Field label={t("agent.flows.addLead.name", { defaultValue: "Buyer name" })}>
            <TextInput
              value={form.name}
              onChange={(v) => setForm((f) => ({ ...f, name: v }))}
              placeholder="e.g. Maya Chen"
            />
          </Field>
          <Field label={t("agent.flows.addLead.phone", { defaultValue: "Phone" })}>
            <TextInput
              mono
              value={form.phone}
              onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
              placeholder="+65 ····"
            />
          </Field>
        </div>
        <Field label={t("agent.flows.addLead.source", { defaultValue: "Source" })}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {SOURCES.map((s) => (
              <Chip
                key={s}
                active={form.source === s}
                onClick={() => setForm((f) => ({ ...f, source: s }))}
              >
                {s}
              </Chip>
            ))}
          </div>
        </Field>
        <Field label={t("agent.flows.addLead.stage", { defaultValue: "Pipeline stage" })}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {PIPELINE_STAGES.map((s) => (
              <Chip
                key={s.value}
                active={form.stage === s.value}
                onClick={() => setForm((f) => ({ ...f, stage: s.value }))}
              >
                {s.label}
              </Chip>
            ))}
          </div>
        </Field>
        <Field
          label={t("agent.flows.addLead.notes", { defaultValue: "Notes" })}
          hint={t("agent.flows.addLead.notesHint", {
            defaultValue: "Anything you've learned in the first contact.",
          })}
        >
          <TextArea
            value={form.notes}
            onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
            rows={3}
            placeholder="e.g. Couple, two kids, downgrading from Sentosa. Budget flexible to $1.45M for high floor."
          />
        </Field>
        <AIHint>
          {t("agent.flows.addLead.ai", {
            defaultValue: "We'll match this buyer against your other case briefs in case they fit elsewhere too.",
          })}
        </AIHint>
      </ModalBody>

      {error && (
        <div
          style={{
            margin: "0 16px 8px",
            padding: "8px 10px",
            background: "var(--ag-bad-bg, rgba(191,56,56,0.08))",
            border: "1px solid var(--ag-bad, #bf3838)",
            borderRadius: 6,
            color: "var(--ag-bad, #bf3838)",
            fontSize: 12.5,
          }}
        >
          {error}
        </div>
      )}

      <ModalFooter leftHint={t("agent.flows.escCancel", { defaultValue: "Esc to cancel" })}>
        <Btn ghost onClick={onClose} disabled={submitting}>
          {t("agent.flows.cancel", { defaultValue: "Cancel" })}
        </Btn>
        <Btn
          primary
          disabled={submitting}
          onClick={async () => {
            setError(null);
            if (!form.name?.trim()) {
              setError(t("agent.flows.addLead.needName", {
                defaultValue: "Buyer name is required.",
              }) as string);
              return;
            }
            setSubmitting(true);
            try {
              // pipeline_stage is now a real field; status defaults to
              // the "lead" temperature for a freshly-added buyer. Source
              // is appended to notes so first-contact context is kept.
              const annotations: string[] = [];
              if (form.source)        annotations.push(`Source: ${form.source}`);
              if (form.notes?.trim()) annotations.push(form.notes.trim());
              await createThread({
                contact_name: form.name.trim(),
                contact_phone: form.phone?.trim() ?? "",
                status: "lead",
                pipeline_stage: form.stage ?? "new_enquiry",
                linked_listing_id: linkedListingId ?? null,
                notes: annotations.join("\n"),
              });
              invalidateCases();
              onClose();
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {submitting
            ? t("agent.flows.addLead.submitting", { defaultValue: "Adding…" })
            : t("agent.flows.addLead.submit", { defaultValue: "Add to pipeline" })}
        </Btn>
      </ModalFooter>
    </ModalShell>
  );
}
